<template>
  <div
    class="lego-table"
    ref="legoTableRef"
    v-columns-resizable="{ header: dumpHeaders, child: false }"
  >
    <div class="lego-table__top">
      <!-- Table Title -->
      <div class="lego-table__title" v-if="title">
        <label class="lego-table__title__label">{{ title }}</label>
        <span class="lego-table__title__desc" v-if="titleDesc">{{ titleDesc }}</span>
      </div>
      <!-- Table Control -->
      <div
        class="lego-table__control"
        :class="controlClass"
        v-show="(isControlOnlySelecting && selected.length !== 0) || !isControlOnlySelecting"
      >
        <div class="lego-table__counter" v-if="!noControl" :class="{ 'left-divider': title }">
          <!--
                  $ver: v0.1.0
                  총 항목 수, 페이지 당 항목 수를 보여주는 영역입니다. 다른 컨텐츠로 대체할 수 있습니다.
                  #This area shows the total number of items and the number of items per page. You can replace it with other content.
              -->
          <slot name="counter-instead">
            <template v-if="productStyle && selected.length > 0"
              ><span class="selected-info"
                >{{ selected.length }} {{ selectedText || t('label.isSelected') }}</span
              ></template
            >
            <template v-if="!productStyle">
              <span class="lego-table__counter__total"
                >{{ totalText || t('label.total') }} <b>{{ $filters.comma(totalCount) }}</b></span
              >
              <div
                class="lego-table__action-selected left-divider"
                v-if="isSelectedControl && selected && isActionSelect && !singleSelect"
              >
                <span>{{ selected.length }} {{ selectedText || t('label.selected') }}</span>
                <span v-if="deselectBtn" class="deselect-btn" @click="clearSelected(true)">{{
                  t('label.deselection')
                }}</span>
              </div>
              <div v-if="targetMaxCheck" class="message">
                {{ messageMaxCnt }}
              </div>
              <div class="left-divider" v-if="!noPage">
                <lego-dropdown
                  class="lego-table__counter__per-page"
                  :items="$_perPageItems"
                  :disabled="items.length === 0"
                  :itemWidth="110"
                  v-model="rowPerPage"
                  borderLess
                  @update:modelValue="(val) => changePage(val, 'dropdown')"
                >
                  <template v-slot:itemText="itemTextScope">
                    <b>{{ itemTextScope.item && itemTextScope.item.value }} </b>
                    <span style="margin-left: 4px; margin-right: 1px">{{
                      perPageText || t('label.perPage')
                    }}</span>
                  </template>
                </lego-dropdown>
              </div>
            </template>
          </slot>
        </div>
        <div
          class="lego-table__action"
          :class="{
            'lego-table__action-none':
              !$slots['action'] &&
              !(selectActionControl && (singleSelect || multiSelect)) &&
              !isRefresh
          }"
        >
          <!--
                  $ver: v0.1.0
                  테이블 우측 상단에 위치하는 영역입니다. 일반적으로 테이블의 추가, 수정, 삭제에 대한 버튼이 위치합니다.
                  #This area is in the upper right corner of the table. In general, there are buttons for adding, modifying, and deleting tables.
              -->
          <slot name="action">
            <template v-if="selectActionControl && (singleSelect || multiSelect)">
              <lego-button sm @click="addItem">{{ t('button.add') }}</lego-button>
              <lego-button sm v-if="modelValue.length" @click="modifySelectedItem"
                >{{ t('button.edit') }}
              </lego-button>
              <lego-button sm v-if="modelValue.length" @click="removeSelectedItem"
                >{{ t('button.remove') }}
              </lego-button>
            </template>
          </slot>
          <div
            v-if="isRefresh"
            class="sc-refresh-btn"
            :class="{ 'sc-refresh-btn-with': $slots['action'] }"
          >
            <sc-icon name="ri-refresh" :size="16" @click="onRefresh" />
            <lego-tooltip mouseOver bottom>{{ t('button.refresh') }}</lego-tooltip>
          </div>
        </div>
        <div class="lego-table__extra-action" v-if="$slots['extra-action']">
          <!--
                  $ver: v0.1.1
                  테이블 우측 상단에 위치하는 영역입니다. action 영역에 추가로 영역이 더 필요할 경우 사용합니다.
                  #This area is in the upper right corner of the table. Use if you need more areas in addition to the action area.
              -->
          <slot name="extra-action" />
        </div>
      </div>
    </div>
    <div class="lego-table__top-second">
      <div class="lego-table__extra-action-row" v-if="$slots['extra-action-row']">
        <!--
                $ver: v0.1.1
                테이블 우측 상단에 위치하는 영역입니다. action 영역에 추가로 영역이 더 필요할 경우 사용합니다.
                #This area is in the upper right corner of the table. Use if you need more areas in addition to the action area.
        -->
        <slot name="extra-action-row" />
      </div>
    </div>
    <div
      class="table-guide"
      ref="resizableContent"
      @mousemove="headerDragOver"
      @dragover="headerDragOver"
      @mouseup="headerDragEnd"
      @dragend="headerDragEnd"
    >
      <div
        v-if="resizeColumn"
        v-show="toggleResize"
        class="lego-table__resize-line"
        :style="resizeLineStyle"
      />
      <div ref="tableHeader" class="header-table">
        <table class="lego-table__area" ref="cloneTable">
          <template v-if="colgroup">
            <colgroup>
              <col
                v-if="selectAll || singleSelect || (multiSelect && !selectAll) || $slots['expand']"
                :width="selectAllWidth + 'px'"
              />
              <template v-for="(header, index) in dumpHeaders">
                <col
                  v-if="!header.children"
                  :key="index"
                  :class="{
                    [header.value]: true
                  }"
                  :style="{
                    width:
                      header.width === 'auto' || header.width === '*' || !header.width
                        ? 'auto'
                        : `${header.width}px`,
                    'min-width': header.minWidth ? header.minWidth + 'px' : '45px'
                  }"
                />
              </template>
              <col v-if="isRowMenuItems" width="57px" />
            </colgroup>
          </template>
          <!-- Header -->
          <thead class="lego-table__header" ref="tableHead" :class="headerClass">
            <!--
$ver: v0.1.0
table 태그 아래 thead 태그 영역입니다. 기본적으로 자동 생성되나, customHeader 속성으로 tr/th 태그로 커스텀 할 수 있습니다.
#Thead tag area below the table tag. It is automatically created by default, but can be customized with the tr / th tag with the customHeader attribute.
-->
            <slot v-if="customHeader" name="header" :attr="headerAttr" />
            <tr class="lego-table__header__row" v-else :style="{ height: headerHeight + 'px' }">
              <!-- @mouseleave="headerMouseLeave" -->
              <th v-if="$slots['expand']" :style="{ width: selectAllWidth + 1 + 'px' }"></th>
              <th
                v-if="selectAll"
                :class="[
                  'lego-table__header-checker',
                  { 'fixed__cell-left': fixedColumn && checkFixed }
                ]"
                :style="{ width: selectAllWidth + 'px' }"
              >
                <lego-checkbox
                  small
                  :disabled="headerCkCheck"
                  :checked="selectAllChecked"
                  :indeterminate="selectAllChecked && selectAllCheckedIcon === 'indeterminate'"
                  @click="clickSelectAll"
                />
              </th>
              <th
                v-if="multiSelect && !selectAll"
                :class="[
                  'lego-table__header-checker',
                  { 'fixed__cell-left': fixedColumn && checkFixed }
                ]"
                :style="{ width: selectAllWidth + 'px' }"
              />
              <th
                v-if="singleSelect"
                :class="[
                  'lego-table__header-checker',
                  { 'fixed__cell-left': fixedColumn && checkFixed }
                ]"
                :style="{ width: selectAllWidth + 'px' }"
              />
              <th
                v-for="(header, hIdx) in dumpHeaders"
                :key="hIdx"
                :style="thStyle(header)"
                :class="thClass(header)"
              >
                <span class="lego-table__th-cell" :class="{ 'remove-evt': toggleResize }">
                  {{ header.text }}
                  <span class="lego-table__icon-required" v-if="header.required">*</span>
                  <span class="infor-icon-container" v-if="header.infor">
                    <sc-icon class="lego-table__icon-infor" name="ri-info" :size="16" />
                    <lego-tooltip top mouseOver>{{ header.infor }}</lego-tooltip>
                  </span>
                  <sc-icon
                    v-if="header.sortable"
                    class="lego-table__icon-sort"
                    :name="sortIcon(hIdx)"
                    :size="16"
                    :class="{ 'lego-table__icon-sort--selected': checkSortSelected(hIdx) }"
                    @click.stop="sortIconClick(hIdx, header)"
                  />
                  <lego-icon
                    v-if="header.filterable"
                    class="lego-table__icon-filter"
                    iconType="df"
                    icon="filter"
                    :size="16"
                    @click.stop="filterIconClick(hIdx)"
                  />
                  <div v-if="!header.sortable" style="height: 26px"></div>
                </span>
                <lego-icon-wrapper class="lego-table__hidden-icon">
                  <lego-icon
                    icon-size="16px"
                    icon="change_width"
                    style="cursor: pointer"
                    @click="header.displayFrom = ''"
                  />
                </lego-icon-wrapper>
                <div
                  class="lego-table__header-resize"
                  v-if="resizeColumn && !header.isNotheaderResize"
                  :style="headerResizeBoxStyle(hIdx)"
                  @dragstart="headerDragStart(hIdx, $event)"
                  @mousedown="headerDragStart(hIdx, $event)"
                />
              </th>
              <th v-if="isRowMenuItems" style="width: 57px"></th>
            </tr>
          </thead>
        </table>
      </div>
      <div class="lego-table__area-wrapper" ref="tableWrapper" :style="bodyStyle">
        <div
          v-if="!isPermission"
          ref="bodyScroll"
          class="set-scroll"
          :class="{ 'hiden-scroll-x': isNotScrollX && !fixedColumn }"
          :style="bodyScrollHeight"
          @scroll="checkBodyScroll"
          @customClick="customClick"
        >
          <table class="lego-table__area">
            <template v-if="colgroup">
              <colgroup>
                <!--
                      $ver: v0.1.0
                      table 태그 아래 colgroup 태그 영역입니다. 기본적으로 전체 체크, 각 컬럼의 헤더에 맞춰서 생성됩니다.
                      #The colgroup tag area below the table tag. By default, all checks are made to match the header of each column.
                  -->
                <slot name="colgroup" />
                <col
                  v-if="
                    selectAll || singleSelect || (multiSelect && !selectAll) || $slots['expand']
                  "
                  :width="selectAllWidth + 'px'"
                />

                <template v-for="(header, index) in dumpHeaders">
                  <col
                    v-if="!header.children"
                    :key="index"
                    :class="header.displayFrom ? 'lego-table__col__from-' + header.displayFrom : ''"
                    :style="{
                      width:
                        header.width === 'auto' || header.width === '*' || !header.width
                          ? 'auto'
                          : `${header.width}px`,
                      'min-width': header.minWidth ? header.minWidth + 'px' : '45px'
                    }"
                  />
                </template>
                <col v-if="isRowMenuItems" width="57px" />
              </colgroup>
            </template>
            <!-- Body -->
            <tbody class="lego-table__body table-extend-body" ref="tableBody" :class="bodyClass">
              <slot name="customRow" :attr="headerAttr" :rowAttr="rowAttr" />
              <tr v-if="singleSelect && (multiSelect || selectAll)" class="lego-table__body__alert">
                <td :colspan="alertColspan">
                  You can't use 'singleSelect prop' with multiSelect or selectAll prop
                </td>
              </tr>
              <slot v-if="customBody" name="body" :rowAttr="rowAttr" />
              <template v-else v-for="(item, iIdx) in items" :key="item[itemId] + '_' + iIdx">
                <tr
                  @click="(e) => onClickRow(item, 'row', iIdx, e)"
                  @mouseover="(e) => onHoverRow(item, 'row', iIdx, e)"
                  @mouseout="(e) => onMouseOutRow(item, 'row', iIdx, e)"
                  :style="tableRowStyle"
                  :id="item[itemId] + '_' + iIdx"
                  :class="{
                    'lego-table__selected': isSelected(item),
                    sortableRow: rowSortable,
                    'lego-table__sorted': item.flag === 'E',
                    'row-hover': rowHover && !item.disabled && !item.cursorDefault,
                    disabled: item.disabled,
                    'row-border-bottom': scrollFixRow && items.length < scrollFixRow
                  }"
                >
                  <!--
$ver: v0.1.0
각 Row 영역입니다. 기본적으로 입력된 데이터에 따라 자동생성되나, <td> 태그를 활용하여 커스텀 할 수 있습니다.
#Each Row area. By default, it is automatically generated based on the data entered, but can be customized using the <td> tag.
-->
                  <slot
                    name="items"
                    :item="item"
                    :index="iIdx"
                    :attr="itemsAttr(item)"
                    :selectAllWidth="selectAllWidth"
                    :children="item.children"
                  >
                    <!-- Default Cell -->
                    <td
                      v-if="multiSelect"
                      :class="checkerClass(item)"
                      :style="{ width: selectAllWidth + 'px' }"
                    >
                      <!-- 191202 backup <lego-checkbox small v-model="value" :value="item"/> -->
                      <lego-checkbox
                        small
                        :valueKey="valueKey"
                        :checked="selected"
                        @update:modelValue="(val) => onClickRowCheckbox(item, val)"
                        :value="item"
                        :disabled="item.disabled || item.checkDisabled"
                      />
                    </td>
                    <td
                      v-if="singleSelect"
                      :class="checkerClass(item)"
                      :style="{ width: selectAllWidth + 'px' }"
                    >
                      <lego-radio
                        small
                        :valueKey="valueKey"
                        @update:modelValue="(val) => onClickRowCheckbox(item, val)"
                        :checked="isSingleCheck(item)"
                        :disabled="item.disabled || item.checkDisabled"
                      />
                    </td>
                    <td
                      v-if="$slots['expand']"
                      :style="{ width: selectAllWidth + 'px' }"
                      class="row-expand"
                    >
                      <lego-icon-wrapper style="width: 16px">
                        <lego-icon
                          class="lego-dropdown row-expand__icon"
                          icon-type="df"
                          :icon="item.expand ? 'arrow_up' : 'arrow_down'"
                          :size:="16"
                          @click="(e) => onClickRow(item, 'expand', iIdx, e)"
                        />
                      </lego-icon-wrapper>
                    </td>
                    <template v-for="(header, hIdx) in dumpHeaders" :key="hIdx">
                      <template v-if="!header.children">
                        <td
                          v-if="
                            !header.checker &&
                            (!header.itemsRowspan || calcRowspan(header.itemsRowspan, iIdx) > 0)
                          "
                          :key="item[itemId] + '_' + iIdx + '_' + hIdx"
                          :style="tdStyle(header, item, iIdx)"
                          :class="tdClass(header)"
                          :rowspan="header.itemsRowspan && calcRowspan(header.itemsRowspan, iIdx)"
                        >
                          <component
                            v-if="typeof header.customCell == 'object'"
                            :is="cloneCustomCell(header.customCell)"
                            :value="item[header.value]"
                            :header="header.value"
                            :iIdx="iIdx"
                            :hIdx="hIdx"
                            :item="item"
                            :keyRender="iIdx + '#' + hIdx"
                          ></component>
                          <v-runtime-template
                            v-else-if="typeof header.customCell == 'function'"
                            :template="header.customCell(item[header.value], iIdx, item)"
                          />
                          <template v-else>
                            <div
                              v-if="header.tooltip"
                              class="value"
                              v-lego-tooltip="header.tooltip(item)"
                            >
                              {{
                                header.defaultValue !== null &&
                                header.defaultValue !== undefined &&
                                (item[header.value] === null ||
                                  item[header.value] === undefined ||
                                  item[header.value] === '')
                                  ? header.defaultValue
                                  : item[header.value]
                              }}
                            </div>
                            <div
                              v-else-if="
                                header.progress && (item[header.value] == 0 || item[header.value])
                              "
                              style="text-align: center"
                            >
                              <span v-if="item[header.value] === -1">{{ '-' }}</span>
                              <div v-else class="table-progress">
                                <div class="table-progress-box">
                                  <!--
외부 설정 필요시
background: header.progress(item[header.value]),
-->
                                  <div
                                    class="table-progress-thum"
                                    :style="{
                                      width:
                                        item[header.value] > 100 ? 100 : item[header.value] + '%',
                                      background:
                                        item[header.value] >= 90
                                          ? '#ef4f44'
                                          : item[header.value] <= 80
                                          ? '#37b284'
                                          : '#fdb525'
                                    }"
                                  />
                                </div>
                                {{ item[header.value] }} %
                              </div>
                            </div>
                            <div v-else class="value" v-lego-tooltip:ellipsis="item[header.value]">
                              {{
                                header.defaultValue !== null &&
                                header.defaultValue !== undefined &&
                                (item[header.value] === null ||
                                  item[header.value] === undefined ||
                                  item[header.value] === '')
                                  ? header.defaultValue
                                  : item[header.value]
                              }}
                            </div>
                          </template>

                          <lego-icon-wrapper class="lego-table__hidden-icon">
                            <lego-icon :size="16" icon-type="df" icon="more" />
                          </lego-icon-wrapper>
                        </td>
                      </template>
                    </template>
                    <!-- Default Cell END -->
                  </slot>
                  <td v-if="isRowMenuItems" style="width: 57px">
                    <lego-drop-holder
                      autoWidth
                      v-show="item.rowMenuItems || rowMenuItems.length > 0"
                      ref="rowMenuDropHolder"
                      class="menu-drop-holder"
                      :items="item.rowMenuItems || rowMenuItems"
                      parent="legoDropHolder"
                      menuDirection="right"
                      appendToBody
                      @click="(val) => rowMenuClick(item, val)"
                      @open="(val) => rowMenuToggle(item, val)"
                    >
                      <lego-icon class="menu-icon" :size="16" icon-type="df" icon="more" />
                    </lego-drop-holder>
                  </td>
                </tr>
                <tr v-if="item.expand" :key="item[itemId] + '_' + iIdx + '_expand'">
                  <!--
$ver: v0.1.0
각 Row 가 펼침/닫힘 처리가 가능할 경우, 펼쳐지는 부분에 대한 컨텐츠 영역 입니다.
#If each row can be expanded / closed, this is the content area for the expanded part.
-->
                  <slot name="expand" :item="item" />
                </tr>
              </template>
            </tbody>
          </table>
          <div
            v-if="infiniteScroll && infiniteHasMore && isShowInfiniteTrigger"
            ref="infiniteTrigger"
            id="infiniteTrigger"
            class="infinite-trigger"
          ></div>
        </div>
        <div
          v-if="isPermission"
          class="lego-table-permission"
          :class="{ 'lego-table-permission-cursor': this.$attrs['permission'] }"
          :style="{ minHeight: this.noDataHeight ? `${this.noDataHeight}px` : '319px' }"
        >
          <sc-icon name="ri-alert" :size="34" style="margin-bottom: 4px" />
          <span @click="$emit('permission')">{{ t('label.permission') }}</span>
        </div>
        <div
          class="set-scroll"
          :class="{ 'hiden-scroll-x': isNotScrollX && !fixedColumn }"
          @scroll="checkBodyScroll"
        >
          <div
            v-if="items.length === 0 && !$slots['customRow']"
            class="lego-table__no-data dev_no-data"
            :style="noDataStyle"
          >
            <div class="table-null-default" :style="tableNull">
              <slot name="no-data">
                <!-- <lego-icon iconType="df" icon="add_box_40" :iconSize="40" /> -->
                <img src="@/assets/img/scmp/no_data.png" />
                <div style="margin-top: 14px">
                  {{ noDataText || t('label.noData') }}
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="customFooter" class="footer-table">
      <table class="lego-table__area">
        <tfoot class="lego-table__footer" ref="tableFoot">
          <slot name="footer" :attr="footerAttr" />
        </tfoot>
      </table>
    </div>
  </div>
</template>
<script>
import VRuntimeTemplate from 'vue3-runtime-template';
import i18nMixin from '@/mixins/i18n.mixin.js';
import {
  find,
  filter,
  has,
  throttle,
  sum,
  findLast,
  isEmpty,
  map,
  findIndex,
  cloneDeep
} from 'lodash';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import CScroll from '../../cmp/cScroll/cScroll.vue';

export default {
  name: 'legoTable',
  emits: [
    'update:modelValue',
    'headerDumpWidth',
    'allChk',
    'sort',
    'change',
    'cusMenu',
    'customClick',
    'inputAll',
    'removeSelectedItem',
    'modifySelectedItem',
    'addItem',
    'rowMenuClick',
    'rowMenuToggle',
    'clickNoDataBtn',
    'infinite',
    'refresh',
    'permission',
    'update:headers',
    'scrolling',
    'hoverRow',
    'mouseOutRow'
  ],
  mixins: [i18nMixin],
  components: {
    VRuntimeTemplate,
    CScroll
  },
  props: {
    rowHeight: { type: [Number, String], default: 32 },
    headerHeight: { type: [Number, String], default: 28 },
    multiRowHeader: { type: Number, default: 0 },
    scrollMaxRow: { type: Number, default: null },
    scrollFixRow: { type: Number, default: null },
    customFooter: { type: Boolean, default: false },
    selectActionControl: { type: Boolean, default: true },
    isSelectedControl: { type: Boolean, default: true },
    selectedReset: { type: Boolean, default: false },
    rowMenuItems: { type: Array, default: () => [] },
    rowMenuWidth: { type: [Number, String], default: 80 },
    deselectBtn: { type: Boolean, default: true },
    rowspanTable: { type: Boolean, default: false },
    colgroup: { type: Boolean, default: true },
    valueKey: { type: [String, Array], default: '' },
    columnBorder: { type: Boolean, default: undefined },
    productNoData: {
      type: [Boolean, Object],
      default: false
    },
    productStyle: { type: Boolean, default: false },
    isControlOnlySelecting: { type: Boolean, default: false },
    infiniteScroll: { type: Boolean, default: false },
    infiniteHasMore: { type: Boolean, default: false },
    isRefresh: { type: Boolean, default: false },
    fixedColumn: { type: Boolean, default: false },
    checkFixed: { type: Boolean, default: false },
    expandFixed: { type: Boolean, default: false },
    noDataHeight: { type: Number, default: undefined },
    isPermission: { type: Boolean, default: false },
    resizeColumn: { type: Boolean, default: true },
    customBody: { type: Boolean, default: false },
    selectMaxCnt: { type: Number, default: 999 },
    isNotScrollX: { type: Boolean, default: false },
    depth: { type: Boolean, default: false },
    messageMaxCnt: { type: String, default: '' },
    sortKey: { type: String, default: '' },
    fixColumnBorder: { type: String, default: '1px solid #bbbdc1' },
    heightTable: { type: Number, default: undefined },
    isHoverRow: { type: Boolean, default: false }
  },
  data: () => ({
    hasTableScroll: false,
    setDefaultSort: false,
    targetMaxCheck: false,
    rowHover: false,
    selectAllWidth: 56,
    default: {
      perPageItems: [
        { text: '10', value: 10 },
        { text: '20', value: 20 },
        { text: '30', value: 30 },
        { text: '40', value: 40 },
        { text: '50', value: 50 },
        { text: '100', value: 100 }
      ]
    },
    rowPerPageFlag: true,
    scrollLeft: 0,
    hasXScroll: false,
    isMmounted: false,
    infiniteObserver: null,
    isInfiniteObserver: false,
    isShowInfiniteTrigger: false,
    infiniteScrollTimer: null,
    columnsWidth: [],
    resized: false,
    toggleResize: false,
    resizeBeforeWidth: 0,
    resizeBeforePositionX: 0,
    resizeTargetIdx: 0,
    resizeLineTop: 0,
    resizeLineLeft: 0,
    resizeLineHeight: 0,
    dumpHeaders: [],
    scrollWidthTable: 0,
    clientWidthTable: 0,
    sortActive: null
  }),
  mounted() {
    if (this.customBody) window.addEventListener('resize', this.buildDumpHeader);
  },
  beforeUnmount() {
    if (this.customBody) window.removeEventListener('resize', this.buildDumpHeader);
  },
  unmounted() {
    if (this.infiniteScroll && this.infiniteObserver) {
      this.infiniteObserver.disconnect();
    }
    if (this.fixedColumn && this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        this.selected = val;
      }
    },
    items: {
      immediate: true,
      deep: true,
      handler() {
        this.selected = this.modelValue;

        this.$nextTick(() => {
          if (!this.isMmounted) {
            if (this.isSelect) {
              this.rowHover = true;
            }
            this.changePage();
            this.initSetInfiniteObserver();

            if (this.fixedColumn) {
              this.tableResizeObserver();
              this.calcTableWidth();

              if (this.depth && this.childTable) {
                this.$nextTick(() => {
                  this.hasXScroll = this.parentXScroll;
                  this.tableWidth = this.parentTableWidth;
                  this.setColumnsWidth();
                });
              }
            }

            this.isMmounted = true;
          } else {
            if (this.infiniteScroll && this.infiniteHasMore && this.isShowInfiniteTrigger) {
              this.closeInfiniteLoading();
            }
          }
          this.setInfiniteObserver();
        });
      }
    },
    'pagination.rowsPerPage': {
      handler: function (val) {
        this.rowPerPage = val;
        this.$nextTick(() => {
          this.changePage(val);
        });
      }
    }
  },
  computed: {
    resizeLineStyle() {
      let leftTable = 0;
      const resizableContent = this.$refs.resizableContent;
      if (resizableContent) {
        leftTable = resizableContent.getBoundingClientRect().left;
      }

      let tmpStyle = {};
      tmpStyle['position'] = 'absolute';
      tmpStyle['height'] = this.resizeLineHeight + 'px';
      tmpStyle['left'] = this.resizeLineLeft - leftTable + 'px';
      tmpStyle['top'] = '0px';
      return tmpStyle;
    },
    isRowMenuItems() {
      const isRowMenuItem = this.items.filter((item) => item.rowMenuItems);

      return isRowMenuItem.length > 0 || this.rowMenuItems.length > 0;
    },
    isSelect() {
      return this.$attrs['onClickRow'];
    },
    totalCount() {
      return this.pagination.totalItems || this.items.length;
    },
    headerContentHeight() {
      return '28px';
    },
    headerClass() {
      return [
        this.customHeader && 'lego-table__header-custom',
        this.customHeader &&
          (this.multiRowHeader === 0 || this.multiRowHeader === 1) &&
          'lego-table__header-custom-single',
        this.multiRowHeader && 'lego-table__header-multi-row'
      ];
    },
    controlClass() {
      let clazz = [];
      if (this.reverseControl) {
        clazz.push('lego-table__control--reverse');
      }
      if (this.noPage) {
        clazz.push('lego-table__control--no-page');
      }
      if (this.productStyle) {
        clazz.push('lego-table__control--product');
      }
      return clazz;
    },
    footerAttr() {
      let styleObject = {};

      this.dumpHeaders.forEach((header) => {
        styleObject[header.value] = {
          style: {
            width: header.width ? header.width + 'px' : 'auto',
            textAlign: header.align && header.align
          },
          colspan: header.colspan
        };
      });

      return styleObject;
    },
    headerAttr() {
      let styleObject = {};
      if (this.multiSelect || this.singleSelect) {
        styleObject.checker = {
          style: {
            width: this.selectAllWidth + 'px'
          },
          rowspan: this.multiRowHeader
        };
      }

      styleObject.expandBtn = {
        style: {
          // width: this.selectAllWidth + 'px',
          position: this.fixedColumn && this.expandFixed && 'sticky',
          right: this.fixedColumn && this.expandFixed && '0px'
        },
        rowspan: this.multiRowHeader,
        class: [
          {
            'expand-btn': true,
            'fixed__cell-right': this.fixedColumn && this.expandFixed,
            'fixed__cell-line': this.fixedColumn && this.expandFixed && this.hasXScroll
          }
        ]
      };

      this.dumpHeaders.forEach((header) => {
        styleObject[header.value] = {
          style: {
            // width: header.width ? header.width + 'px' : 'auto',
            textAlign: header.headerAlign && header.headerAlign,
            ...this.thStyle(header)
          },
          colspan: header.colspan,
          rowspan: header.rowspan,
          class: this.thClass(header)
        };
      });

      return styleObject;
    },
    rowAttr() {
      return {
        style: {
          height: this.rowHeight + 'px'
        },
        class: {
          'row-hover': this.rowHover
        }
      };
    },
    checkedLen() {
      const ableItems = this.items.filter((item) => !item.disabled && !item.checkDisabled);
      let selectedLen = 0;
      ableItems.forEach((item) => {
        if (this.valueKey) {
          let containItems;
          if (typeof this.valueKey === 'object') {
            containItems = this.selected.filter((selectedItem) => {
              return this.isCheckValueKeyTypeOfArray(item, selectedItem);
            });
          } else {
            containItems = this.selected.filter(
              (selectedItem) => item[this.valueKey] === selectedItem[this.valueKey]
            );
          }

          if (containItems.length > 0) {
            selectedLen += 1;
          }
        } else if (find(this.selected, item)) {
          selectedLen += 1;
        }
      });

      if (selectedLen === ableItems.length && ableItems.length > 0) {
        return 'all';
      } else if (selectedLen > 0) {
        return 'some';
      }
      return 0;
    },
    selectAllCheckedIcon() {
      if (this.checkedLen === 'all') return 'checked';
      else return 'indeterminate';
    },
    selectAllChecked() {
      return this.checkedLen !== 0;
    },
    bodyStyle() {
      const bodyStyle = {};

      if (this.items.length === 0 && !this.$slots['customRow']) {
        bodyStyle.borderTop = 0;
      }

      return bodyStyle;
    },
    bodyScrollHeight() {
      const bodyHeight = {};

      if (this.scrollMaxRow) {
        bodyHeight.maxHeight = `${this.scrollMaxRow * 32}px`;
      }

      if (this.scrollFixRow && this.items.length > 0) {
        bodyHeight.height = `${this.scrollFixRow * 32}px`;
      } else {
        bodyHeight.height = 'auto';
      }

      return bodyHeight;
    },
    $_perPageItems() {
      let items = this.perPageItems || this.default.perPageItems;
      const itemClones = this.$util.deepCopy(items);
      const perPageText = this.perPageText || this.t('label.perPage');
      itemClones.forEach((item) => {
        item.text += ` ${perPageText}`;
      });

      return itemClones;
    },
    bodyClass() {
      return {
        'column-border':
          typeof this.columnBorder === 'undefined' ? this.rowspanTable : this.columnBorder
      };
    },
    tableRowStyle() {
      return {
        height: this.tableRowHeight
      };
    },
    noDataStyle() {
      const scrollRow = this.scrollFixRow || this.scrollMaxRow;
      let minHeight = null;
      if (this.noDataHeight) {
        minHeight = `${this.noDataHeight}px`;
      } else {
        minHeight = scrollRow ? `${scrollRow * 32}px` : null;
      }
      return {
        minHeight: minHeight,
        width: this.scrollWidthTable ? `${this.scrollWidthTable}px` : null
      };
    },
    tableNull() {
      return {
        width: this.clientWidthTable ? `${this.clientWidthTable}px` : null
      };
    },
    headerCkCheck() {
      let checkboxList = filter(this.items, (item) => {
        return item.disabled || item.checkDisabled;
      });
      return checkboxList.length === this.items.length || false;
    },
    filteredHeaders() {
      if (this.customHeader && this.multiRowHeader) {
        return this.dumpHeaders.filter(
          (header) => !(header.children && header.children.length > 0)
        );
      } else {
        return this.dumpHeaders;
      }
    }
  },
  methods: {
    cloneCustomCell(data) {
      return cloneDeep(data);
    },
    changePages() {
      this.$refs.bodyScroll.scrollTop = 0;
    },
    headerResizeBoxStyle(hIdx) {
      let tmpStyle = {};
      // tmpStyle['height'] = this.headerContentHeight;
      if (hIdx + 1 === this.dumpHeaders.length) {
        tmpStyle['display'] = 'none';
        tmpStyle['pointer-events'] = 'none';
      }
      return tmpStyle;
    },
    headerDragStart(targetIdx, event) {
      if (this.toggleResize) return;
      this.toggleResize = true;
      this.resized = true;
      let $pEL = event.target.parentElement;
      this.resizeBeforeWidth = $pEL.getBoundingClientRect().width;
      this.resizeBeforePositionX = $pEL.getBoundingClientRect().left + this.resizeBeforeWidth;
      this.resizeTargetIdx = targetIdx;
      this.resizeLineLeft = this.resizeBeforePositionX - 2;
      this.resizeLineTop = this.$refs.resizableContent.getBoundingClientRect().top;
      this.resizeLineHeight = this.$refs.resizableContent.getBoundingClientRect().height;
      // this.resizeLineLeft = event.layerX + 'px';
    },
    headerDragEnd(event) {
      if (this.toggleResize) {
        let nWidth = Number(event.pageX - this.resizeBeforePositionX) + this.resizeBeforeWidth;
        const headerWidthSet = this.dumpHeaders;
        const minW = headerWidthSet[this.resizeTargetIdx].minWidth ?? 45;
        const minSizeCk = nWidth <= minW;
        headerWidthSet[this.resizeTargetIdx].width = minSizeCk ? minW : nWidth;
        headerWidthSet[this.resizeTargetIdx].isMin = minSizeCk;

        if (this.isNotScrollX) {
          // Calculate the changed length of the column
          let widthResize = Number((minSizeCk ? minW : nWidth) - this.resizeBeforeWidth);

          const resizeFor = (index) => {
            // The position in reference that is larger than the number of columns returns the size of the original column
            if (index >= headerWidthSet.length) {
              headerWidthSet[this.resizeTargetIdx].width =
                headerWidthSet[this.resizeTargetIdx].width - widthResize;
              return;
            }
            // If the column in reference does not allow a size change then move on to the next column
            if (headerWidthSet[index].isNotResize || headerWidthSet[index].isNotheaderResize) {
              resizeFor(index + 1);
            } else {
              const widthAfter = headerWidthSet[index].width;
              const minWidthAfter = headerWidthSet[index].minWidth ?? 45;

              let newWidthAfter = widthAfter - widthResize;
              newWidthAfter = newWidthAfter < minWidthAfter ? minWidthAfter : newWidthAfter;
              headerWidthSet[index].isMin = newWidthAfter <= minWidthAfter;
              widthResize = newWidthAfter - widthAfter + widthResize;
              headerWidthSet[index].width = newWidthAfter;
              // If you have changed the reference column size that the "widthResize" value is still valid then continue to the next column
              if (widthResize !== 0) {
                resizeFor(index + 1);
              }
            }
          };
          resizeFor(this.resizeTargetIdx + 1);
        }

        this.headerUpdate(headerWidthSet);
        this.toggleResize = false;
      }
    },
    headerUpdate(dump) {
      this.$emit('update:headers', dump);
      if (this.isWidthAuto) {
        this.buildDumpHeader();
      }
      if (this.infiniteScroll) {
        this.setInfiniteObserver();
      }
    },
    headerDragOver(event) {
      if (this.resizeColumn && this.toggleResize) {
        const table = this.$refs.resizableContent;
        const posX = event.pageX;
        this.resizeLineLeft = posX + table.scrollLeft;
        this.resizeLineHeight = this.$refs.resizableContent.getBoundingClientRect().height;
      }
    },
    async buildDumpHeader() {
      let childRen = 0;
      if (this.isNotScrollX) {
        await this.convertColumMetric();
      }
      this.dumpHeaders = filter(cloneDeep(this.headers), (header) => !header.hide);

      this.dumpHeaders.forEach((header, index) => {
        if (header.children) childRen = childRen + header.children.length;
        if (header.isNotResize && index > 0) {
          this.dumpHeaders[index - 1].isNotheaderResize = true;
        }
      });

      if (this.resizeColumn && !isEmpty(this.dumpHeaders) && this.dumpHeaders.length > 1) {
        this.$nextTick(() => {
          let lastIdx =
            this.multiRowHeader > 0
              ? this.dumpHeaders.length + childRen - 1
              : this.dumpHeaders.length - 1;

          let lastDump =
            this.multiRowHeader > 0 && this.dumpHeaders[this.dumpHeaders.length - 1].children
              ? this.dumpHeaders.length - childRen + 1
              : this.dumpHeaders.length - 1;

          // not resize column의  앞 column의 width를 auto 로 설정
          for (let i = this.dumpHeaders.length - 1; i > 1; i--) {
            if (!this.dumpHeaders[i].isNotResize) {
              lastDump = i;
              break;
            }
          }

          const resizeFor = (itemList, type, offsetWidth, cols) => {
            itemList.forEach((child) => {
              this.dumpHeaders.forEach((head, index) => {
                if (child === head.value) {
                  if (head.children) {
                    resizeFor(head.children, type, offsetWidth, cols);
                  } else {
                    if (type === 'auto') {
                      this.dumpHeaders[index].width = 'auto';
                    } else {
                      this.dumpHeaders[index].minWidth = offsetWidth / cols;
                    }
                  }
                }
              });
            });
          };

          if (this.dumpHeaders[lastDump].children) {
            resizeFor(this.dumpHeaders[lastDump].children, 'auto');
          } else if (!this.isNotScrollX) {
            this.dumpHeaders[lastDump].width = 'auto';
          }

          if (!this.resized && !this.childTable) {
            const headDomLen = this.$refs.tableHead.children[0].children.length;
            const lastCell =
              this.$refs.tableHead.children[0].children[
                headDomLen - 1 < lastIdx ? headDomLen - 1 : lastIdx
              ];
            if (!this.dumpHeaders[lastDump].minWidth) {
              if (this.multiRowHeader > 0) {
                if (this.dumpHeaders[lastDump].children) {
                  resizeFor(
                    this.dumpHeaders[lastDump].children,
                    'min',
                    lastCell.offsetWidth,
                    this.dumpHeaders[lastDump].children.length
                  );
                } else {
                  //this.dumpHeaders[lastDump].minWidth = lastCell.offsetWidth;
                }
              } else {
                //this.dumpHeaders[lastDump].minWidth = lastCell.offsetWidth;
              }
            }
          }
        });
      }

      if (!this.setDefaultSort) {
        //Set Default Sort
        this.dumpHeaders.forEach((header, index) => {
          if (header.desc || header.asc) {
            this.sortIdx = index;
          }
        });
      }

      if (this.fixedColumn) {
        this.setColumnsWidth(this.columnsWidth);
      }

      this.$nextTick(() => {
        if (!this.childTable) {
          const dumpHeadersWidth = [];
          const headerList = this.$refs.tableHead.children[0].children;

          Array.prototype.forEach.call(headerList, (header) => {
            dumpHeadersWidth.push(header.offsetWidth);
          });

          this.$emit('headerDumpWidth', dumpHeadersWidth);
        }
        const tableHeader = this.$refs.tableHeader;
        if (tableHeader && tableHeader.scrollWidth !== tableHeader.clientWidth) {
          this.scrollWidthTable = tableHeader.scrollWidth;
          this.clientWidthTable = tableHeader.clientWidth;
        } else {
          this.scrollWidthTable = 0;
          this.clientWidthTable = 0;
        }
      });
    },
    checkSortSelected(hIdx) {
      let sortIdx = this.sortIdx;
      if (this.sortKey && this.sortActive && this.sortIdx !== -1) {
        sortIdx = this.dumpHeaders.findIndex((item) => item.id === this.sortActive[this.sortKey]);
      }
      return hIdx === sortIdx;
    },
    sortIcon(hIdx) {
      let sortIdx = this.sortIdx;
      if (this.sortKey && this.sortActive && this.sortIdx !== -1) {
        sortIdx = this.dumpHeaders.findIndex((item) => item.id === this.sortActive[this.sortKey]);
        if (hIdx === sortIdx) {
          this.dumpHeaders[hIdx].desc = this.sortActive.desc;
          this.dumpHeaders[hIdx].asc = this.sortActive.asc;
        }
      }
      if (hIdx === sortIdx) {
        if (this.dumpHeaders[hIdx].desc) {
          return 'ri-sort-up';
        } else {
          return 'ri-sort-down';
        }
      } else {
        return 'ri-sort';
      }
    },
    sortIconClick(hIdx, item) {
      legoEventBus.$emit('click-outside');
      this.setDefaultSort = true;

      //다른 column 초기화
      this.dumpHeaders.forEach((header, i) => {
        if (i !== hIdx) {
          header.asc = false;
          header.desc = false;
        }
      });

      if (this.sortableType === 'triple') {
        if (this.dumpHeaders[hIdx].asc) {
          // asc -> desc
          this.dumpHeaders[hIdx].asc = false;
          this.dumpHeaders[hIdx].desc = true;
          this.sortIdx = hIdx;
        } else if (this.dumpHeaders[hIdx].desc) {
          // asc -> default
          this.dumpHeaders[hIdx].desc = false;
          this.sortIdx = -1;
        } else {
          // default -> asc
          this.dumpHeaders[hIdx].asc = true;
          this.sortIdx = hIdx;
        }
      } else {
        // sortableType (dual)
        this.dumpHeaders[hIdx].desc = !!this.dumpHeaders[hIdx].asc;
        this.dumpHeaders[hIdx].asc = !this.dumpHeaders[hIdx].asc;
        this.sortIdx = hIdx;
      }
      if (this.sortKey) {
        this.sortActive = item;
      }
      this.$emit('sort', this.dumpHeaders[hIdx]);
    },
    changePage(val, target) {
      if (target && val === this.pagination.rowsPerPage) return;
      this.pagination.rowsPerPage = this.rowPerPage;

      if (this.rowPerPageFlag) {
        if (val) {
          this.pagination.totalPages = Math.ceil(this.pagination.totalItems / val);

          this.$emit('change', val);
        } else {
          this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.rowPerPage);
        }
        this.rowPerPageFlag = true;
      }

      if (target === 'dropdown') {
        this.rowPerPageFlag = false;
      } else {
        this.rowPerPageFlag = true;
      }
    },
    customClick(idx, val) {
      legoEventBus.$emit('click-outside');
      if (typeof idx === 'object') {
        this.$emit('cusMenu', { ...this.items[idx.tableIdx], ...idx });
        return;
      }
      this.$emit('customClick', this.items[idx], val);
    },
    thStyle(header) {
      let h = {};
      if (this.fixedColumn && header.fixed) {
        const headerIndex = this.filteredHeaders.indexOf(header);

        h['z-index'] = header.fixed === 'left' ? 1000 - headerIndex : 9;
        h['left'] = header.fixed === 'left' && this.calcFixedPos(header) + 'px';
        h['right'] = header.fixed === 'right' && this.calcFixedPos(header) + 'px';
      }
      h['text-align'] = header.headerAlign ? header.headerAlign : 'center';
      // if (!this.resizeColumn && header.width) {
      //   h['width'] = header.width + 'px';
      // }
      return h;
    },
    tdStyle(header, item, iIdx, checkColspan) {
      let h = {};

      if (this.fixedColumn && header.fixed) {
        const headerIndex = this.dumpHeaders.indexOf(header);

        h['z-index'] = header.fixed === 'left' ? 1000 - headerIndex : 9;
        if (checkColspan) {
          h['left'] = 0;
        } else {
          h['left'] = header.fixed === 'left' && this.calcFixedPos(header, checkColspan) + 'px';
        }

        h['right'] = header.fixed === 'right' && this.calcFixedPos(header) + 'px';
      }

      // h['width'] =
      //   header.width === '*' || header.width === 'auto' || !header.width
      //     ? 'auto'
      //     : `${header.width}px`;
      // h['min-width'] = header.minWidth ? `${header.minWidth}px` : `8px`;

      h['text-align'] = header.bodyAlign && header.bodyAlign;
      h['borderBottomWidth'] =
        header.itemsRowspan &&
        this.calcRowspan(header.itemsRowspan, iIdx) + iIdx === this.items.length &&
        this.calcRowspan(header.itemsRowspan, iIdx) > 1
          ? '0px'
          : '1px';
      if (has(header, 'isEvent')) {
        h['pointer-events'] = header.isEvent(item[header.value], iIdx) ? '' : 'none';
      }
      if (header.notUsePadding) {
        h['padding'] = 'unset';
      }
      return h;
    },
    thClass(header) {
      let clazz = ['lego-table__th'];
      clazz.push(header.displayFrom ? 'lego-table__col__from-' + header.displayFrom : '');
      clazz.push(header.isMin ? 'lego-table__col__min' : '');
      if (this.fixedColumn) {
        clazz.push(this.fixedClass(header));
      }

      return clazz;
    },
    tdClass(header) {
      let clazz = [];
      if (header.displayFrom) {
        clazz.push('lego-table__col__from-' + header.displayFrom);
      }

      if (this.fixedColumn) {
        clazz.push(this.fixedClass(header));
      }

      // clazz.push(header.isMin ? 'lego-table__col__min' : '');

      return clazz;
    },
    checkerClass(item) {
      let clazz = ['lego-table__cell-checker'];

      if (item.disabled || item.checkDisabled) {
        clazz.push('cell-checker--disabeld');
      }

      if (this.fixedColumn && this.checkFixed) {
        clazz.push('fixed__cell-left');
      }
      return clazz;
    },
    itemsAttr() {
      let styleObject = {};
      if (this.multiSelect || this.singleSelect) {
        styleObject.checker = {
          style: {
            width: this.selectAllWidth + 'px'
          }
        };
      }

      this.dumpHeaders.forEach((header) => {
        styleObject[header.value] = {
          style: {
            width: header.width && header.width + 'px',
            textAlign: header.align && header.align
          }
        };
      });

      return styleObject;
    },
    calcRowspan(itemsRowspan, index) {
      let rowspanArray = [];

      itemsRowspan.forEach((item) => {
        rowspanArray[item.itemIndex] = item.rowspan;
        for (let i = item.itemIndex + 1; i < item.itemIndex + item.rowspan; i++) {
          rowspanArray[i] = 0;
        }
      });

      return rowspanArray[index] === undefined ? 1 : rowspanArray[index];
    },
    clearSelected(btn) {
      if (this.selectedReset || btn) {
        this.selected = [];
        this.targetMaxCheck = false;
        this.$emit('update:modelValue', this.selected);
      }
    },
    clearErrorMessage() {
      this.targetMaxCheck = false;
    },
    isSelected(row) {
      if (this.modelValue) {
        if (this.modelValue.length) {
          if (this.valueKey) {
            if (typeof this.valueKey === 'object') {
              return (
                this.modelValue.filter((item) => {
                  return this.isCheckValueKeyTypeOfArray(item, row);
                }).length > 0
              );
            }
            return (
              this.modelValue.filter((item) => item[this.valueKey] === row[this.valueKey]).length >
              0
            );
          } else {
            return find(this.modelValue, row);
          }
        } else {
          if (this.valueKey) {
            if (typeof this.valueKey === 'object') {
              return (
                this.modelValue.filter((item) => {
                  return this.isCheckValueKeyTypeOfArray(item, row);
                }).length > 0
              );
            }
            return this.modelValue[this.valueKey] === row[this.valueKey];
          } else {
            return this.modelValue === row;
          }
        }
      }
      return false;
    },
    clickSelectAll() {
      legoEventBus.$emit('click-outside');
      this.targetMaxCheck = false;
      const ableItems = this.items.filter((item) => !item.disabled && !item.checkDisabled);
      this.$emit('allChk');
      if (this.checkedLen === 'all') {
        if (this.valueKey) {
          if (typeof this.valueKey === 'object') {
            this.selected = this.selected.filter(
              (item) =>
                ableItems.filter((ableItem) => {
                  return this.isCheckValueKeyTypeOfArray(item, ableItem);
                }).length === 0
            );
          } else {
            this.selected = this.selected.filter(
              (item) =>
                ableItems.filter((ableItem) => ableItem[this.valueKey] === item[this.valueKey])
                  .length === 0
            );
          }
        } else {
          this.selected = this.selected.filter((item) => !find(ableItems, item));
        }

        this.$emit('update:modelValue', this.selected);
      } else {
        let selectedItem = cloneDeep(this.selected);
        ableItems.forEach((v) => {
          if (this.valueKey) {
            const findItemCheck = selectedItem.filter((accItem) => {
              if (typeof this.valueKey === 'object') {
                return this.isCheckValueKeyTypeOfArray(accItem, v);
              }
              return accItem[this.valueKey] === v[this.valueKey];
            });
            if (findItemCheck.length === 0) {
              selectedItem.push(v);
            }
          } else {
            if (!find(selectedItem, v)) {
              selectedItem.push(v);
            }
          }
        });
        if (selectedItem.length > this.selectMaxCnt) {
          this.targetMaxCheck = true;
          this.$emit('inputAll', this.selected, this.targetMaxCheck);
        } else {
          this.selected = ableItems.reduce((acc, cur) => {
            if (this.valueKey) {
              if (
                acc.filter((accItem) => {
                  if (typeof this.valueKey === 'object') {
                    return this.isCheckValueKeyTypeOfArray(accItem, cur);
                  }
                  return accItem[this.valueKey] === cur[this.valueKey];
                }).length > 0
              ) {
                return acc;
              } else {
                return [...acc, cur];
              }
            } else {
              if (find(acc, cur)) {
                return acc;
              } else {
                return [...acc, cur];
              }
            }
          }, this.selected);
          this.$emit('update:modelValue', this.selected);
        }
      }
    },
    onClickRow(row, target, rowIndex, e) {
      if (row.rowMenuItems || this.rowMenuItems.length > 0) {
        const rowEl = this.$refs.tableWrapper.querySelectorAll('tr')[rowIndex];
        const dropholder = rowEl.querySelector('.lego-drop-holder');
        if (dropholder.contains(e.target) || dropholder === e.target) {
          return;
        }
      }
      if (target === 'row' && this.isSelect) {
        this.$attrs['onClickRow'](row);
      }

      if (target === 'expand') {
        this.onClickRowExpand(row);
      }
    },
    onHoverRow(row) {
      if (this.isHoverRow) {
        this.$emit('hoverRow', row);
      }
    },
    onMouseOutRow(row) {
      if (this.isHoverRow) {
        this.$emit('mouseOutRow', row);
      }
    },
    onClickRowCheckbox(row, value) {
      legoEventBus.$emit('click-outside');
      if (!this.multiSelect && !this.singleSelect) {
        return;
      }

      if (!this.multiSelect) {
        this.selected = [row];
      } else {
        if (value.length > this.selectMaxCnt) {
          this.targetMaxCheck = true;
        } else {
          this.targetMaxCheck = false;
          this.selected = value;
        }
      }

      this.$emit('update:modelValue', this.selected, this.targetMaxCheck);
    },
    isSingleCheck(item) {
      if (this.valueKey) {
        if (typeof this.valueKey === 'object') {
          return (
            this.modelValue.filter((valueItem) => {
              return this.isCheckValueKeyTypeOfArray(item, valueItem);
            }).length > 0
          );
        }
        return (
          this.modelValue.filter((valueItem) => valueItem[this.valueKey] === item[this.valueKey])
            .length > 0
        );
      } else {
        return find(this.modelValue, item) ? true : false;
      }
    },
    removeSelectedItem() {
      this.$emit('removeSelectedItem', this.modelValue);
    },
    modifySelectedItem() {
      this.$emit('modifySelectedItem', this.modelValue);
    },
    addItem() {
      this.$emit('addItem');
    },
    rowMenuClick(row, val) {
      this.$emit('rowMenuClick', row, val);
    },
    rowMenuToggle(row, val) {
      this.$emit('rowMenuToggle', row, val);
    },
    checkBodyScroll(e) {
      const target = e.target;

      if (target.scrollLeft !== this.scrollLeft) {
        this.scrollLeft = target.scrollLeft;
        if (this.$refs.tableHeader) {
          this.$refs.tableHeader.scrollLeft = e.target.scrollLeft;
        }

        this.closeDropMenu(e);
      }
    },
    closeDropMenu(e) {
      const _throttle = throttle(() => {
        this.$emit('scrolling', e);
      }, 1000);
      _throttle();
    },
    clickNoDataBtn() {
      this.$emit('clickNoDataBtn');
    },
    callInfiniteScroll() {
      if (this.infiniteScrollTimer) {
        clearTimeout(this.infiniteScrollTimer);
      }

      this.$loader.open('infiniteTrigger', 'xs', false);
      this.$emit('infinite');
    },
    closeInfiniteLoading() {
      this.$nextTick(() => {
        if (this.$refs.infiniteTrigger && this.$refs.infiniteTrigger.childElementCount) {
          this.$loader.close('infiniteTrigger', 'xs', false);

          const scrollEl = this.$refs.bodyScroll;
          const scrollElPos = scrollEl.scrollTop;
          this.infiniteScrollTimer = setTimeout(() => {
            if (scrollElPos !== scrollEl.scrollTop) scrollEl.scrollTop = scrollElPos;
          }, 100);
        }
      });
    },
    initSetInfiniteObserver() {
      if (this.infiniteScroll && this.infiniteHasMore) {
        const options = {
          root: null,
          threshold: 0
        };

        this.infiniteObserver = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            this.callInfiniteScroll();
          }
        }, options);
      }
    },
    setInfiniteObserver() {
      const scrollEl = this.$refs.bodyScroll;
      if (scrollEl && this.infiniteScroll && this.infiniteHasMore) {
        if (scrollEl.clientHeight < scrollEl.scrollHeight) {
          this.isShowInfiniteTrigger = true;
        } else {
          this.isShowInfiniteTrigger = false;
        }
      }

      this.$nextTick(() => {
        if (this.$refs.infiniteTrigger) {
          this.$refs.infiniteTrigger.style.width = this.$refs.tableBody.clientWidth + 'px';
          this.infiniteObserver.observe(this.$refs.infiniteTrigger);
          this.isInfiniteObserver = true;
        }
      });
    },
    onRefresh() {
      this.$emit('refresh');
    },
    setColumnsWidth() {
      if (this.parentColumsWidth && this.parentColumsWidth.length > 0) {
        this.columnsWidth = this.parentColumsWidth;
        return;
      }

      this.$nextTick(() => {
        if (this.$refs.tableHead) {
          let thList = this.$refs.tableHead.querySelectorAll('th');

          thList = Array.prototype.slice
            .call(thList)
            .filter(
              (th) => th.colSpan === 1 || (th.children && th.children.length > 0) || !th.parent
            );

          this.columnsWidth = [];
          if (this.customHeader && this.multiRowHeader) {
            thList = Array.prototype.slice.call(thList).filter((th) => th.colSpan === 1);
          } else {
            thList = Array.prototype.slice
              .call(thList)
              .filter(
                (th) => th.colSpan === 1 || (th.children && th.children.length > 0) || !th.parent
              );
          }

          this.filteredHeaders.forEach((header, index) => {
            if (!thList[index]) return;
            let width = thList[index].clientWidth;
            if (this.multiSelect || this.singleSelect) {
              if (!thList[index + 1]) return;
              width = thList[index + 1].clientWidth;
            }
            this.columnsWidth.push(width);
          });
        }
      });
    },
    calcFixedPos(header) {
      let result = 0;

      const selectTdWidth =
        (this.multiSelect || this.singleSelect) && this.checkFixed ? this.selectAllWidth : 0;

      const expandTdWidth = this.depth && this.expandFixed ? this.selectAllWidth : 0;

      const index = this.dumpHeaders.indexOf(header);

      if (header.fixed === 'left') {
        const sumArray = this.columnsWidth.slice(0, index);
        result += selectTdWidth;
        result += sum(sumArray);
        result += sumArray.length;
      }
      if (header.fixed === 'right') {
        const sumArray = this.columnsWidth.slice(index + 1, this.columnsWidth.length + 1);
        result += expandTdWidth;
        result += sum(sumArray);
      }
      return result;
    },
    fixedClass(header) {
      const leftLast = findLast(this.dumpHeaders, (item) => item.fixed === 'left');
      const rightFirst = find(this.dumpHeaders, (item) => item.fixed === 'right');
      const headerIndex = this.dumpHeaders.indexOf(header);
      const nextHeader = this.dumpHeaders[headerIndex + 1];
      return {
        'fixed__cell-left': header.fixed === 'left',
        'fixed__cell-right': header.fixed === 'right',
        'fixed__cell-left--last': this.hasXScroll && leftLast && header.value === leftLast.value,
        'fixed__cell-right--first':
          this.hasXScroll && rightFirst && header.value === rightFirst.value,
        'not-line':
          this.hasXScroll &&
          !header.fixed &&
          ((nextHeader && nextHeader.fixed) || (this.expandFixed && !nextHeader))
      };
    },
    calcTableWidth() {
      this.$nextTick(() => {
        if (this.$refs.tableWrapper) {
          this.tableWidth = this.$refs.tableWrapper.clientWidth;
        }
        if (this.$refs.bodyScroll) {
          this.hasXScroll = this.$refs.bodyScroll.clientWidth < this.$refs.bodyScroll.scrollWidth;
        }
      });
    },
    tableResizeObserver() {
      if (!this.childTable) {
        this.resizeObserver = new ResizeObserver(() => {
          this.calcTableWidth();
          this.setColumnsWidth(this.columnsWidth);
        });

        this.$nextTick(() => {
          if (this.$refs.tableWrapper) {
            this.resizeObserver.observe(this.$refs.tableWrapper);
          }
        });
      }
    },
    convertColumMetric() {
      const countMetric = this.headers.filter((item) => !(item.width > 0)).length;
      this.$nextTick(() => {
        const el = this.$refs.tableWrapper;

        if (countMetric === 0 || !el) return;

        const tableWidth = el.clientWidth;
        const isCheckBox =
          this.selectAll || this.singleSelect || (this.multiSelect && !this.selectAll);

        const initialValue = isCheckBox || this.$slots['expand'] ? this.selectAllWidth : 0;

        const widthNotMetric = this.headers.reduce((accumulator, currentValue) => {
          if (currentValue.width > 0) {
            return accumulator + currentValue.width;
          }
          return accumulator;
        }, initialValue);

        if (widthNotMetric > tableWidth) {
          this.headers.map((item) => {
            if (!(item.width > 0) && countMetric > 0) {
              // set width default 200px
              return (item.width = 200);
            }
          });
          return;
        }
        const widthScroll = this.scrollMaxRow ? 6 : 0;
        this.headers.map((item) => {
          if (!(item.width > 0) && countMetric > 0) {
            const newWidth = (tableWidth - widthNotMetric - widthScroll) / countMetric;
            return (item.width = newWidth > 200 ? newWidth : 200);
          }
        });
      });
    },
    isCheckValueKeyTypeOfArray(item, itemCheck) {
      let check = true;
      for (let i = 0; i < this.valueKey.length; i++) {
        if (item[this.valueKey[i]] !== itemCheck[this.valueKey[i]]) {
          check = false;
          break;
        }
      }
      return check;
    }
  }
};
</script>
<style lang="scss" scoped>
$color-border: #bbbdc1;

.lego-table {
  overflow: visible;

  .table-guide {
    overflow: hidden;
    position: relative;
    .lego-table__resize-line {
      border-left: 1px solid rgba(0, 0, 0, 0.6);
      z-index: 1001;
    }
  }

  .sc-container-table__action {
    & + .lego-button {
      margin-left: 8px;
    }
  }

  &-permission {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    background: #fafbfd;
    border-top: 1px solid $color--ui05;
    color: rgba($color--ui, 0.4);
    font-size: 14px;
    font-weight: $lego__font-weight--medium;

    &-cursor {
      > span {
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    > span {
      text-align: center;
      white-space: pre-wrap;
    }
  }

  .set-scroll {
    @include scroll();
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(#989898, 0.4);
      border-radius: 5px;
      background-clip: padding-box;
      border-left: 7px solid transparent;
      border-right: 0px solid transparent;
      border-top: 5px solid transparent;
      border-bottom: 5px solid transparent;
      &:hover {
        background-color: rgba(#989898, 0.8);
      }
    }
    overflow-x: auto;
  }

  .hiden-scroll-x {
    overflow-x: hidden;
  }

  &__top {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    .lego-table__action {
      position: relative;

      &-none {
        &:before {
          display: none !important;
        }
      }

      &:before {
        content: '';
        height: 10px;
        width: 1px;
        background-color: $color-border;
        display: inline-block;
        margin-right: 8px;
      }
    }

    .sc-refresh-btn {
      display: flex;
      align-items: center;

      .sc-icon {
        margin-left: 0;
        opacity: 0.8;
        cursor: pointer;

        &:hover {
          opacity: 1;
        }
      }

      &-with {
        .sc-icon {
          margin-left: 8px;
        }
      }
    }

    .lego-table__title {
      height: 32px;
    }

    .left-divider {
      &:before {
        position: relative;
        display: inline-block;
        margin: 0 8px;
        border-left: 1px solid $color--ui07;
      }
    }
  }

  .lego-table__control {
    height: inherit;
    flex: 1;
    z-index: 1001;

    &--product {
      background: var(--color-primary-tint01);
      border-radius: 6px;
      height: 80px;
      display: flex;
      align-items: center;
      padding: 0 40px;
      margin-bottom: 12px;

      .selected-info {
        margin-right: 20px;
      }
    }
  }

  .lego-table__counter {
    margin-right: 8px;

    &__total {
      b {
        color: var(--color-ui-ui01);
      }
    }

    .lego-table__action-selected {
      display: flex;
      align-items: center;
      margin-right: 0;
      font-weight: $lego__font-weight--bold;

      .deselect-btn {
        color: $color--ui06;
        cursor: pointer;
        margin-left: 8px;
        font-weight: $lego__font-weight--normal;
        line-height: 18px;
        border-bottom: 1px solid $color--ui06;

        &:hover,
        &:active {
          color: $color--ui01;
          border-bottom: 1px solid $color--ui01;
        }
      }
    }
  }

  &__counter__per-page {
    :deep .lego-dropdown__title {
      padding-left: 0;
      padding-right: 0;
      background-color: transparent;

      .lego-dropdown__title-icon {
        margin-left: 6px;
      }

      &:focus {
        &::before {
          display: none;
        }
      }
    }
  }

  .lego-table__area-wrapper {
    position: relative;
    // overflow-y: auto;
    // overflow-x: hidden;
    border-top: $lego-table__body__border-top;
    border-bottom: $lego-table__body__border-bottom;

    :deep .simplebar-offset {
      width: 100%;
    }

    :deep .simplebar-track {
      &.simplebar-vertical {
        background: $color--white;
      }
    }
  }

  .lego-table__area {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    table-layout: fixed;

    .lego-table__header {
      tr {
        &.lego-table__header__row {
          border-top: 0;
          border-bottom: 0;

          .lego-table__th {
            position: relative;

            &-cell {
              word-break: keep-all;
              display: inline-flex;
              align-items: center;
              vertical-align: middle;
              max-width: 100%;
              overflow: hidden;

              &.remove-evt {
                pointer-events: none;
              }

              > .sc-icon {
                cursor: pointer;
                margin-left: 4px;
                display: inline-block;
                height: 16px;
                width: 16px;
                position: relative;
                top: 1px;
                flex: 0 0 16px;

                &.lego-table__icon-sort {
                  height: 28px;
                  font-size: 16px;
                  line-height: 24px;
                  vertical-align: middle;

                  :deep svg {
                    vertical-align: middle;
                  }
                }
              }
            }

            &.lego-table__col__min {
              background-color: var(--color-ui-ui10);
            }
          }
        }
      }

      :deep th {
        border-right: $lego-table__column__border;
        background: #f8f8fb;

        &:last-child,
        &.not-line {
          border-right: 0;
        }

        .lego-table__header-resize {
          z-index: 1;
          position: absolute;
          top: 0;
          right: -2px;
          width: 4px;
          height: 100vh;
          cursor: w-resize;

          &:hover {
            background-color: #d2d3d6;
          }
        }
      }
      :deep .lego-table__icon-sort {
        font-size: 16px;
        vertical-align: middle;
        margin-top: -2px;

        &:not(.lego-table__icon-sort--selected) {
          color: $color--ui04;
        }
      }

      :deep .lego-table__icon-infor {
        opacity: 0.6;
        position: relative;
        top: -1px;
        margin-left: 4px;

        &:hover {
          opacity: 1;
        }
      }

      .infor-icon-container {
        & + .lego-table__icon-sort {
          margin-left: 0;
        }
      }

      .lego-table__icon-required {
        margin-left: 4px;
        color: $color--status--notification;
      }
    }

    .lego-table__header-custom {
      &-single {
        > tr {
          height: 28px;
        }
      }

      &.lego-table__header-multi-row {
        th[colspan] {
          border-bottom: $lego-table__contents__border-bottom;
        }

        th {
          height: 30px;
          overflow: hidden;

          &.lego-table__col__min {
            background-color: var(--color-ui-ui10);
          }
        }

        tr {
          &:nth-child(n + 2) {
            th:last-child {
              border-right: $lego-table__column__border;
            }
          }
        }
      }
    }

    .lego-table__body {
      td {
        border-bottom: $lego-table__contents__border-bottom;
        background: #fff;
        border-right: 0px solid transparent;

        &.lego-table__col__min {
          background-color: var(--color-ui-ui10);
        }

        &:not(.expand-btn) {
          @include ellipsis;
        }

        &:last-child {
          border-right: 0;
        }

        > a {
          color: $color--primary;

          &.link {
            text-decoration: underline;
            cursor: pointer;
          }
        }

        .value {
          @include ellipsis;
        }
      }

      tr {
        border-bottom: 0;

        &:last-child:not(.row-border-bottom) {
          > td {
            border-bottom: 0;
          }
        }

        &:hover {
          background: none;

          td {
            background: #eef2fc;
          }
        }

        &.lego-table__selected {
          td {
            background: #e1e8fa;

            :deep(.more-action-icon) {
              display: none;
            }
          }
        }

        &.row-hover {
          cursor: pointer;

          &:hover:not(.lego-table__selected) {
            td {
              background: #eef2fc;
            }
          }
        }

        .lego-table__cell-checker {
          pointer-events: none;
          text-overflow: inherit;

          &:not(.cell-checker--disabeld) {
            .lego-input {
              pointer-events: auto;
            }
          }
        }
      }

      &.column-border {
        td {
          border-right: 1px solid $color--ui08;

          &:last-child {
            border-right: 0;
          }
        }
      }
    }

    .lego-table__header,
    .lego-table__body {
      th {
        font-size: 12px;
      }

      th {
        &.fixed__cell-left,
        &.fixed__cell-right {
          position: sticky !important;
          z-index: 9;
        }

        &.fixed__cell-left {
          left: 0;
          border-right: 1px solid $color-border;

          &--last {
            border-right: 1px solid $color-border;
          }
        }

        &.fixed__cell-right {
          right: 0;

          &--first {
            border-left: 1px solid $color-border;
          }

          &.fixed__cell-line {
            &.expand-btn {
              border-left: 1px solid $color-border;
            }
          }

          & + .expand-btn {
            border-left: 0 !important;
          }
        }
      }

      td {
        &.fixed__cell-left,
        &.fixed__cell-right {
          position: sticky !important;
          z-index: 9;
        }

        &.fixed__cell-left {
          left: 0;
          border-right: v-bind(fixColumnBorder);

          &--last {
            border-right: v-bind(fixColumnBorder);
          }
        }

        &.fixed__cell-right {
          right: 0;

          &--first {
            border-left: v-bind(fixColumnBorder);
          }

          &.fixed__cell-line {
            &.expand-btn {
              border-left: 1px solid $color-border;
            }
          }

          & + .expand-btn {
            border-left: 0 !important;
          }
        }
      }
    }
  }

  .header-table {
    position: relative;
    background-color: $lego-table__header__background-color;
    border-top: $lego-table__header__border-top;
    overflow: hidden;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 1px;
      background: $lego-table__header__background-color;
      z-index: -1;
    }
  }

  .lego-table__no-data {
    display: block;
    min-height: 96px;
    height: 96px;
    background: #fafbfd;
    border-top: 1px solid $color--ui05;
    color: #737A8c;
    font-size: 14px;
    font-weight: 400;

    > span {
      text-align: center;
      white-space: pre-wrap;
    }

    :deep .lego-icon {
      color: rgba($color--ui01, 0.2);
    }

    .product-list-no-data {
      padding: 191px 0;
      text-align: center;

      .info-title {
        font-weight: 700;
        color: var(--color-ui-ui01);
        font-size: 20px;
        margin-bottom: 4px;
      }

      .info-sub-title {
        color: var(--color-ui-ui04);
        line-height: 22px;
        font-weight: 400;
      }

      :deep .lego-icon {
        margin: 16px 0 24px;
      }
    }
  }

  .infinite-trigger {
    position: relative;
    height: 52px;
    border-top: $lego-table__contents__border-bottom;

    :deep .vld-background {
      background-color: transparent;
    }
  }

  .footer-table {
    margin-top: -1px;
    background: $lego-table_footer_background-color;

    tr {
      border-bottom: $lego-table__contents__border-bottom;
      height: 32px;

      td {
        padding: 0 16px;
        @include font($size: 14px);
      }
    }
  }
}
</style>
<style lang="scss">
.lego-table {
  .lego-table__area {
    .lego-table__body {
      &.table-extend-body {
        > tr {
          border-bottom: 0;

          td {
            border-bottom: $lego-table__contents__border-bottom;
          }

          &.disabled {
            td:not(.lego-table__cell-checker) {
              position: relative;
              cursor: default;

              > * {
                color: rgba($color--ui01, 0.3);
              }

              .menu-drop-holder {
                pointer-events: none;
              }

              .menu-icon {
                color: rgba($color--ui01, 0.3);
              }
            }
          }

          &:last-child {
            // background: #000;
            > td {
              border-bottom: $lego-table__contents__border-bottom !important;
            }

            &:not(.row-border-bottom) {
              > td {
                border-bottom: 0;
              }
            }
          }
        }
      }
    }
  }

  .table-progress {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .table-progress-box {
      flex: 0 0 calc(100% - 45px);
      width: calc(100% - 45px);
      height: 8px;
      border-radius: 4px;
      background-color: var(--color-chart-00);
      margin-right: 4px;
    }

    .table-progress-thum {
      height: 8px;
      border-radius: 4px;
    }
  }

  .sc-columns-resizable {
  }

  .columns-resize-bar {
    &:hover {
      background-color: #d2d3d6;
    }

    &:active {
      width: 1px !important;
      background-color: rgba(#000, 0.6);
    }
  }
}
.table-null-default {
  position: sticky;
  left: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.row-expand {
  user-select: all;
  .row-expand__icon {
    min-width: unset !important;
  }
}
</style>
